import { Component, Host, h, Element, State, Listen, Watch, Prop } from '@stencil/core';
import { getCookie, setCookie } from '../../utils/cookie';
import { isPlatform } from '@baloise/ds-core';
import { DataService } from '../../utils/data-service.service';

@Component({
  tag: 'bam-back-teaser-list',
  styleUrl: 'bam-back-teaser-list.scss',
  shadow: false,
})

/*
Types and Insurances:
"mf": Motorfahrzeug insurance
"bdh": Haushalt & Haftpflicht, Privathaftpflicht insurance
"ygh": Youngo insurance
"bdr": Reise insurance
"bdrs": Rechtsschutz insurance
*/
export class BamBackTeaserList {
  @Element() el: HTMLElement;

  /**
   * If `true` the back teaser list restart
   */
  @Prop({ mutable: true, reflect: true }) restart = false;

  private backTeasers;
  private infoBoxes;
  private notificationsWrapper;
  private multipleBackBoxesInsurances;
  private multipleLocalStorageInsurances;
  private sortedBackBoxesPrios;
  private localStorageSortedArr;
  private DEV: boolean = false;
  private isMarketingAccepted: boolean = false;

  private dateTest;

  @State() rerender: boolean = false;

  @State() isTouch: boolean;

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.setIsTouch();
  }

  @Watch('restart')
  async watchRestartHandler(newValue: boolean, oldValue: boolean) {
    this.DEV && console.log('watchRestartHandler NEW VALUE', newValue);
    this.DEV && console.log('watchRestartHandler OLD VALUE', oldValue);
    this.rerender = newValue;
  }

  setIsTouch() {
    this.isTouch = isPlatform(window, 'touch');
  }

  componentDidLoad() {
    const urlParams = new URLSearchParams(window.location.search);
    this.DEV = urlParams.has('devmode');

    if (this.DEV) {
      let dateStr = urlParams.has('date') ? urlParams.get('date') : '08-25-2023';
      dateStr = dateStr.replace(/-/g, ' ');
      this.dateTest = new Date(dateStr);
      let backBoxTypes = urlParams.has('types') ? urlParams.get('types') : '';
      this.setDevCookieOptanonConsent(); //SIMULATE DEV MARKETING COOKIE ACCEPTANCE
      this.setDevLocalStorageValues(backBoxTypes); // SIMULATE DEV LOCAL STORAGE VALUES PRESENCE
    }

    this.notificationsWrapper = document.querySelector('bam-notifications-wrapper');
    this.backTeasers = this.getBackTeasers();
    this.infoBoxes = this.getInfoBoxes();

    this.isMarketingAccepted = this.checkMarketingCookie();
    this.toggleShowHideBox();
  }

  componentDidUpdate() {
    if (this.rerender) {
      this.backTeasers = this.getBackTeasers();
      this.infoBoxes = this.getInfoBoxes();
      this.toggleShowHideBox();
      this.restart = false;
    }
  }

  connectedCallback() {
    this.setIsTouch();
  }

  private listenToSnackbarCloseEvent = async () => {
    this.DEV && console.log('SNACKBAR CLOSE EVENT');
    this.rerender = true;
  };

  /**
   * Main logic to show or to remove the back teaser based on conditions
   * If the back teaser can be shown, calls the function to set the higher prio back teaser to active
   */
  toggleShowHideBox() {
    let lessThanTwoInfoBoxes = (this.infoBoxes && this.infoBoxes.length < 2) || !this.infoBoxes;
    this.multipleBackBoxesInsurances = this.getMultipleBackBoxesTypesAndPrio();
    this.multipleLocalStorageInsurances = this.getMultipleLocalStorageInsurances();
    this.sortedBackBoxesPrios = this.getBackBoxTypeWithHigherPrio();
    this.localStorageSortedArr = this.getLocalInsuranceSortedPrioTypes();
    this.filterOutNotValidBackTeasers();
    const withBackTeasers = this.backTeasers && this.backTeasers.length > 0;
    const withInfoBoxes = this.infoBoxes && this.infoBoxes.length > 0;
    if (withBackTeasers && withInfoBoxes) {
      this.infoBoxes.forEach(infoBox => {
        infoBox.addEventListener('balClose', this.listenToSnackbarCloseEvent);
      });
    } else if (!withBackTeasers && withInfoBoxes) {
      this.infoBoxes.forEach(infoBox => {
        infoBox.removeEventListener('balClose', this.listenToSnackbarCloseEvent);
      });
    }
    const showBackBox = lessThanTwoInfoBoxes && this.isMarketingAccepted && this.localStorageSortedArr.length > 0 && withBackTeasers;
    showBackBox && this.setActiveHigherPrioBackBox();
  }

  /**
   * If back box cookies is there calls the method to delete it and updates the local storage array
   * If back box is not in local storage removes it from DOM
   * and updates back boxes list
   */
  filterOutNotValidBackTeasers() {
    this.backTeasers.forEach(box => {
      const cname = `notification-back-${box.type}-`;
      const cookieIsThere = getCookie(cname) !== '';
      const type = box.type;
      const emptyLocalStorage =
        this.getLocalStorageItem(`${type}_recurrent_user_encrypted_quote_id`) === null || this.getLocalStorageItem(`${type}_recurrent_user_product_code`) === null;
      if (cookieIsThere) {
        this.removeBackBox(box);
        this.updateLocalStorageArr(cname, '');
      }
      if (emptyLocalStorage) {
        this.removeBackBox(box);
      }
    });
    this.backTeasers = this.getBackTeasers();
  }

  private getBackTeasers(): HTMLBamBackTeaserElement[] | null {
    return Array.from(this.el.querySelectorAll('bam-back-teaser')) as HTMLBamBackTeaserElement[];
  }

  private getInfoBoxes(): HTMLBalSnackbarElement[] | null {
    let infoBoxesArr = this.isTouch
      ? (Array.from(this.notificationsWrapper.querySelectorAll('bam-snackbar')) as HTMLBalSnackbarElement[])
      : (Array.from(this.notificationsWrapper.querySelectorAll('bal-snackbar')) as HTMLBalSnackbarElement[]);
    return infoBoxesArr.filter(
      item => item.children.length > 0 && (item.color === 'warning' || item.color === 'info') && item.clientHeight > 0 && !item.classList.contains('hidden'),
    );
  }

  /**
   * Set the back teaser with higher Prio on active
   */
  setActiveHigherPrioBackBox() {
    this.backTeasers.forEach(box => {
      if (box.type === this.localStorageSortedArr[0].type) {
        const encodedProductCode = this.getEncodedProductCode(box.type);
        const hausaltExtraPackage = this.getHausaltExtraPackage(box.type);
        const motorcycleExtraPackage = this.getMotorcycleExtraPackage(box.type);
        const boxUrl = `${box.url}?product_code=${encodedProductCode}${hausaltExtraPackage}${motorcycleExtraPackage}`;
        const boxQuoteId = encodeURIComponent(this.getLocalStorageItem(`${box.type}_recurrent_user_encrypted_quote_id`));
        const multipleBackBoxes = this.localStorageSortedArr.length > 1;
        const setBoxActive = () => {
          box.url = boxUrl;
          box.quoteId = boxQuoteId;
          box.active = true;
        };
        DataService.getData(boxUrl).then(data => {
          if (data.link) {
            box.icon = data.icon;
            box.customText = data.label;
            box.btnCalculationLink = data.calculationLink;
            box.btnUrlFirstPart = data.link;
            this.DEV && console.log('FETCH HIGHER PRIO', box.type);
            if (multipleBackBoxes) {
              const secondPrioType = this.localStorageSortedArr[1].type;
              const secondEncodedProductCode = this.getEncodedProductCode(secondPrioType);
              const secondHausaltExtraPackage = this.getHausaltExtraPackage(secondPrioType);
              const secondMotorcycleExtraPackage = this.getMotorcycleExtraPackage(secondPrioType);
              const secondHigherPrioUrl = `${box.url}?product_code=${secondEncodedProductCode}${secondHausaltExtraPackage}${secondMotorcycleExtraPackage}`;
              // DEVMODE testing next prio data.link: const secondHigherPrioUrl = `${box.url}`
              const fetchSecondHigherPrio =
                this.getLocalStorageItem(`${secondPrioType}_recurrent_user_product_code`) !== null &&
                this.getLocalStorageItem(`${secondPrioType}_recurrent_user_encrypted_quote_id`) !== null;
              fetchSecondHigherPrio &&
                DataService.getData(secondHigherPrioUrl)
                  .then(secondData => {
                    if (secondData.link) {
                      this.DEV && console.log('FETCH SECOND HIGHER PRIO', secondPrioType);
                      box.secondPrioLink = secondData.link.toString() + encodeURIComponent(this.getLocalStorageItem(`${secondPrioType}_recurrent_user_encrypted_quote_id`));
                      this.backTeasers.forEach(item => {
                        if (item.type === secondPrioType) {
                          const nextLinkWrapperEl = item.querySelector('[data-js-item="next-prio-link-wrapper"]') as HTMLElement;
                          const nextLinkEl = !!nextLinkWrapperEl ? (nextLinkWrapperEl.querySelector('a') as HTMLElement) : null;
                          const nextLinkElSpan = !!nextLinkEl ? nextLinkEl.querySelector('span') : null;
                          const firstText = !!nextLinkEl ? nextLinkEl.getAttribute('data-js-link-content') : '';
                          const secondText = !!nextLinkEl && !!nextLinkElSpan ? nextLinkElSpan.textContent : '';
                          box.secondPrioLinkText = `${firstText} ${secondText}`;
                        }
                      });
                    } else {
                      this.removeBoxAndUpdateData(null, secondPrioType);
                    }
                  })
                  .then(() => setBoxActive());
            } else {
              setBoxActive();
            }
          } else {
            this.removeBoxAndUpdateData(box, box.type);
          }
        });
      }
    });
  }

  removeBoxAndUpdateData(box, type) {
    !!box && this.removeBackBox(box);
    this.updateLocalStorageArr('', type);
    this.localStorageSortedArr.length > 0 && this.setActiveHigherPrioBackBox();
  }

  /**
   * Returns the encoded product code of the specified insurance type
   */
  getEncodedProductCode(type): string {
    return encodeURIComponent(this.getLocalStorageItem(`${type}_recurrent_user_product_code`));
  }

  /**
   * Returns the hausalt insurance extra package for the specified bdh type
   */
  getHausaltExtraPackage(type): string {
    return type === 'bdh' && this.getLocalStorageItem(`${type}_recurrent_user_product_code`) !== null && this.getLocalStorageItem(`${type}_recurrent_user_package`) !== null
      ? `&product_package=${this.getLocalStorageItem(`${type}_recurrent_user_package`)}`
      : '';
  }

  /**
   * Returns the motorcycle insurance extra package for the specified type
   */
  getMotorcycleExtraPackage(type): string {
    return this.getLocalStorageItem(`${type}_recurrent_user_vehicle_type`) !== null && this.getLocalStorageItem(`${type}_recurrent_user_vehicle_type`) === 'motorcycle'
      ? '&product_package=motorcycle'
      : '';
  }

  /**
   * Removes the back teaser from the dom
   */
  removeBackBox(box) {
    box.remove();
  }

  /**
   * Update the local storage Array
   */
  updateLocalStorageArr(cname: string, boxType?: string) {
    for (let i = 0; i < this.localStorageSortedArr.length; i++) {
      let type = this.localStorageSortedArr[i].type;
      if ((cname !== '' && cname.startsWith(`notification-back-${type}-`)) || (boxType !== '' && boxType === type)) {
        const index = this.localStorageSortedArr.indexOf(this.localStorageSortedArr[i]);
        if (index > -1) {
          this.localStorageSortedArr.splice(index, 1);
        }
      }
    }
  }

  /**
   * Returns the prio sorted local storage insurances types
   */
  getLocalInsuranceSortedPrioTypes() {
    let tempArr = [];
    this.multipleLocalStorageInsurances.forEach(item => {
      let newArrObj = { type: '', prio: 0 };
      this.sortedBackBoxesPrios.forEach(sortedItem => {
        if (item === sortedItem.type) {
          newArrObj.type = item;
          newArrObj.prio = sortedItem.prio;
          tempArr.push(newArrObj);
        }
      });
    });
    let sortedTempArr = tempArr.map(item => parseInt(item.prio, 10)).sort((a, b) => a - b);
    return this.getSortedPrioArr(tempArr, sortedTempArr);
  }

  /**
   * Returns the prio sorted template back boxes insurances types
   */
  getBackBoxTypeWithHigherPrio() {
    let sortedPrioArr = this.multipleBackBoxesInsurances.map(item => parseInt(item.prio, 10)).sort((a, b) => a - b);
    let tempNeedSortArr = this.multipleBackBoxesInsurances;
    return this.getSortedPrioArr(tempNeedSortArr, sortedPrioArr);
  }

  /**
   * Returns the sorted array between two arrays: first needs to be sorted, second one sorted
   */
  getSortedPrioArr(needSortArr, sortedReferenceArr) {
    needSortArr.sort(function (a, b) {
      return sortedReferenceArr.indexOf(a.prio) < sortedReferenceArr.indexOf(b.prio) ? -1 : 1;
    });
    return needSortArr;
  }

  /**
   * Returns the insurances types from local storage
   * if the user last visit not older than 90days ago
   * and if local storage insurances types are there
   */
  getMultipleLocalStorageInsurances() {
    let keys = Object.keys(localStorage),
      localStorageInsurancesArray = [];
    const backBoxesInsurances = [...new Set(this.multipleBackBoxesInsurances.map(item => item.type))];
    keys.forEach(item => {
      backBoxesInsurances.forEach(insuranceItemType => {
        // create valid insurances array from localStorage
        // if the localStorage values are there and the last_visit date is not older than 90days ago
        const lastVisit = this.getLocalStorageItem(`${insuranceItemType}_recurrent_user_last_visit`);
        const localStorageIsEmpty =
          this.getLocalStorageItem(`${insuranceItemType}_recurrent_user_encrypted_quote_id`) === null ||
          this.getLocalStorageItem(`${insuranceItemType}_recurrent_user_product_code`) === null;
        if (lastVisit !== null && !localStorageIsEmpty) {
          const dateNow = new Date();
          const lastVisitDate = new Date(lastVisit);
          const oldLocalStorage = Math.floor(this.calculateDaysPassed(lastVisitDate, dateNow)) > 90;
          if (item.startsWith(`${insuranceItemType}`) && !oldLocalStorage) {
            localStorageInsurancesArray.push(insuranceItemType);
          }
        }
      });
    });
    return [...new Set(localStorageInsurancesArray)];
  }

  /**
   * Returns the item in the local storage
   */
  getLocalStorageItem(item) {
    return localStorage.getItem(item);
  }

  /**
   * Returns the amount of days passed between date1(past) and date2(recent/actual)
   */
  calculateDaysPassed(date1, date2) {
    const time_difference = date2.getTime() - date1.getTime();
    return time_difference / (1000 * 60 * 60 * 24);
  }

  /**
   * Returns the template back boxes types and prios
   */
  getMultipleBackBoxesTypesAndPrio() {
    let backBoxesTypesPrioArr = [];
    this.backTeasers.forEach(item => {
      let backBoxTypePrioObj = { type: '', prio: 0 };
      backBoxTypePrioObj.type = item.type;
      backBoxTypePrioObj.prio = item.prio;
      backBoxesTypesPrioArr.push(backBoxTypePrioObj);
    });
    const removeDuplicateObjects = (array, key) => {
      const set = new Set();
      return array.filter(item => {
        const alreadyHas = set.has(item[key]);
        set.add(item[key]);
        return !alreadyHas;
      });
    };
    return removeDuplicateObjects(backBoxesTypesPrioArr, 'type');
  }

  /**
   * checkMarketingCookie
   * check if the marketing has been accepted
   * @returns boolean
   */
  checkMarketingCookie() {
    const optanonConsentCookieString = decodeURIComponent(getCookie('OptanonConsent'));
    if (optanonConsentCookieString !== '' && optanonConsentCookieString !== undefined) {
      const optanonCookieObj = this.getJsonObj(optanonConsentCookieString, '&', '=');
      const optanonCookieGroupObj = this.getJsonObj(optanonCookieObj[0].groups, ',', ':');
      return Boolean(parseInt(optanonCookieGroupObj[0].C0003));
    }
    return false;
  }

  /**
   * getJsonObj
   * transform a string into a valid JSON object
   * returns JSON obj
   * @param {string} str - the initial string value
   * @param {string} separator1 - the first separator of each property
   * @param {string} separator2 - the inner separator of each value
   *
   */
  getJsonObj(str: string, separator1: string, separator2: string) {
    let jsonString = '[{';
    let items = str.split(separator1);
    for (let i = 0; i < items.length; i++) {
      let current = items[i].split(separator2);
      jsonString += '"' + current[0] + '":"' + current[1] + '",';
    }
    jsonString = jsonString.substr(0, jsonString.length - 1);
    jsonString += '}]';
    return JSON.parse(jsonString);
  }

  /**
   * DEV * Set the Marketing cookie acceptance for development/testing purposes
   */
  setDevCookieOptanonConsent() {
    let cname = 'OptanonConsent';
    let cvalue =
      'isGpcEnabled=0&datestamp=Thu+Jul+15+2021+13%3A20%3A19+GMT%2B0200+(Central+European+Summer+Time)&version=6.19.0&isIABGlobal=false&hosts=&consentId=12213940-868e-4eed-9b20-3af74cd2e018&interactionCount=1&landingPath=NotLandingPage&groups=C0001%3A1%2CC0003%3A1%2CC0002%3A0%2CC0004%3A0&geolocation=%3B&AwaitingReconsent=false';
    // let cvalue =
    //   'isGpcEnabled=0&datestamp=Thu+Jul+15+2021+13%3A20%3A19+GMT%2B0200+(Central+European+Summer+Time)&version=6.19.0&isIABGlobal=false&hosts=&consentId=12213940-868e-4eed-9b20-3af74cd2e018&interactionCount=1&landingPath=NotLandingPage&groups=C0004%3A1%2CC0003%3A0%2CC0001%3A1%2CC0002%3A0&geolocation=%3B&AwaitingReconsent=false';
    /*let cvalue =
      'isGpcEnabled=0&datestamp=Thu+Sep+16+2021+14%3A08%3A06+GMT%2B0200+(Central+European+Summer+Time)&version=3.6.18&isIABGlobal=false&hosts=&consentId=12213940-868e-4eed-9b20-3af74cd2e018&interactionCount=1&landingPath=NotLandingPage&groups=1%3A1%2C2%3A1%2C4%3A1%2C101%3A1%2C102%3A1%2C103%3A1%2C104%3A1%2C105%3A1%2C106%3A1%2C107%3A1%2C108%3A1%2C109%3A1&geolocation=%3B&AwaitingReconsent=false';*/
    let expire = '365';

    setCookie(cname, cvalue, expire);
  }

  /**
   * DEV * Set the LocalStorage Key/Value of the insurances types declared in the url for development/testing purposes
   * @param {string} types * from url params f.e. ?devmode&types=auto;privat
   */
  setDevLocalStorageValues(types) {
    types.split(';').forEach(item => {
      let values = {};
      switch (item) {
        case 'auto':
          values = {
            mf_recurrent_user_language: 'de',
            mf_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            mf_recurrent_user_product_code: 'BaloiseDirectMotorfahrzeug',
            mf_recurrent_user_product_name: 'BaloiseDirect Motorfahrzeug',
            mf_recurrent_user_salespoint_code: 'SalesPointCodeAuto',
            mf_recurrent_user_vehicle_make: 'VW',
            mf_recurrent_user_vehicle_type: 'car',
            mf_recurrent_user_vehicle_model: 'Golf TSI 1.0 Comfortline DSG',
            mf_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'motorcycle':
          values = {
            mf_recurrent_user_language: 'de',
            mf_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            mf_recurrent_user_product_code: 'BaloiseDirectMotorfahrzeug',
            mf_recurrent_user_product_name: 'BaloiseDirect Motorfahrzeug',
            mf_recurrent_user_salespoint_code: 'SalesPointCodeAuto',
            mf_recurrent_user_vehicle_make: 'VW',
            mf_recurrent_user_vehicle_type: 'motorcycle',
            mf_recurrent_user_vehicle_model: 'Harley Davidson',
            mf_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'privat':
          values = {
            bdh_recurrent_user_language: 'de',
            bdh_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            bdh_recurrent_user_product_code: 'BaloiseDirectHaushalt',
            bdh_recurrent_user_subproduct_code: 'SubProductCodePrivat',
            bdh_recurrent_user_product_name: 'BaloiseDirect Privat',
            bdh_recurrent_user_salespoint_code: 'SalesPointCodePrivat',
            bdh_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'privat-home': //has extra package _recurrent_user_package
          values = {
            bdh_recurrent_user_language: 'de',
            bdh_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            bdh_recurrent_user_product_code: 'BaloiseDirectHaushalt',
            bdh_recurrent_user_subproduct_code: 'SubProductCodeHome',
            bdh_recurrent_user_product_name: 'BaloiseDirect Home',
            bdh_recurrent_user_salespoint_code: 'SalesPointCodeHome',
            bdh_recurrent_user_last_visit: this.dateTest.toString(),
            bdh_recurrent_user_package: 'HHLiability1',
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'youngo':
          values = {
            ygh_recurrent_user_language: 'de',
            ygh_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            ygh_recurrent_user_product_code: 'BaloiseYounGoHaushalt',
            ygh_recurrent_user_subproduct_code: 'SubProductCodeYoungo',
            ygh_recurrent_user_product_name: 'BaloiseDirect Youngo',
            ygh_recurrent_user_salespoint_code: 'SalesPointCodeYoungo',
            ygh_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'travel':
          values = {
            bdr_recurrent_user_language: 'de',
            bdr_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            bdr_recurrent_user_product_code: 'BaloiseDirectReise',
            bdr_recurrent_user_subproduct_code: 'SubProductCodeTravel',
            bdr_recurrent_user_product_name: 'BaloiseDirect Travel',
            bdr_recurrent_user_salespoint_code: 'SalesPointCodeTravel',
            bdr_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
        case 'legal':
          values = {
            bdrs_recurrent_user_language: 'de',
            bdrs_recurrent_user_encrypted_quote_id: 'olslDgD8yp1YlkbSxZXlsQ==',
            bdrs_recurrent_user_product_code: 'BaloiseDirectRechtsschutz',
            bdrs_recurrent_user_subproduct_code: 'SubProductCodeLegal',
            bdrs_recurrent_user_product_name: 'BaloiseDirect Legal',
            bdrs_recurrent_user_salespoint_code: 'SalesPointCodeLegal',
            bdrs_recurrent_user_last_visit: this.dateTest.toString(),
          };
          this.setSimulateLocalStorageItems(values);
          break;
      }
    });
  }

  /**
   * DEV *  Set each insurance key/value in local storage
   */
  setSimulateLocalStorageItems(values) {
    Object.entries(values).forEach(([key, value]) => typeof value === 'string' && localStorage.setItem(key, value));
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
